import type { QuizQuestion } from '@/types';

interface QuestionCardProps {
  question: QuizQuestion;
  selectedOptionId: string | undefined;
  onSelect: (optionId: string) => void;
}

/**
 * Renders a single quiz question with radio-button options.
 *
 * Design decisions:
 * - Pure presentational component – all state lives in the parent (TurniejPage).
 *   This makes it trivial to test and reuse.
 * - `<fieldset>` + `<legend>` gives correct screen-reader semantics for a radio group.
 * - Each option uses a hidden native `<input type="radio">` – the visible styled
 *   element is a sibling `<label>` styled via `peer` Tailwind utilities.
 *   This preserves native keyboard navigation and accessibility.
 */
export const QuestionCard = ({ question, selectedOptionId, onSelect }: QuestionCardProps) => {
  return (
    <div className="w-full">
      <p className="mb-6 text-base font-semibold leading-relaxed text-gray-800 sm:text-lg">
        {question.text}
      </p>

      <fieldset>
        <legend className="sr-only">Wybierz odpowiedź</legend>
        <div className="flex flex-col gap-3">
          {question.options.map((option) => {
            const isSelected = selectedOptionId === option.id;
            return (
              <label
                key={option.id}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 px-4 py-3 transition-colors ${
                  isSelected
                    ? 'border-competition-accent bg-competition-accent/10'
                    : 'border-gray-200 bg-gray-50 hover:border-competition-accent/50 hover:bg-competition-accent/5'
                }`}
              >
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option.id}
                  checked={isSelected}
                  onChange={() => onSelect(option.id)}
                  className="sr-only"
                />
                <span
                  className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold transition-colors ${
                    isSelected
                      ? 'border-competition-accent bg-competition-accent text-white'
                      : 'border-gray-300 bg-white text-gray-500'
                  }`}
                >
                  {option.label}
                </span>
                <span className="text-sm leading-relaxed text-gray-700 sm:text-base">
                  {option.text}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
};
